/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState, useContext } from 'react';
import API from '../api/api';
import { AuthContext } from '../contexts/AuthContext';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Stack,
  CircularProgress,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Chip,
  MenuItem,
  useMediaQuery,
  useTheme,
  IconButton,
  InputAdornment,
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { useSnackbar } from '../contexts/SnackbarContext';
import { useI18n } from '../contexts/I18nContext';

export default function Users() {
  const { user } = useContext(AuthContext);
  const { showSnackbar } = useSnackbar();
  const { lang } = useI18n();
  const tr = (fr, ar) => (lang === 'ar' ? ar : fr);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({ username: '', password: '', role: 'EMPLOYEE' });
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [resetUserId, setResetUserId] = useState(null);
  const [resetPassword, setResetPassword] = useState('');
  const [randomPassword, setRandomPassword] = useState('');
  const [showResetPassword, setShowResetPassword] = useState(false);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const res = await API.get('/users');
      setUsers(res.data || []);
    } catch (err) {
      showSnackbar(err.response?.data?.message || tr('Erreur lors du chargement des utilisateurs', 'فشل تحميل المستخدمين'), 'error');
    } finally {
      setLoading(false);
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchUsers();
  }, []);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await API.post('/users', form);
      setForm({ username: '', password: '', role: 'EMPLOYEE' });
      showSnackbar(tr('Utilisateur cree', 'تم إنشاء المستخدم'), 'success');
      fetchUsers();
    } catch (err) {
      showSnackbar(err.response?.data?.message || tr('Erreur lors de la creation', 'فشل الإنشاء'), 'error');
    }
  };

  const handleToggleActive = async (userId, active) => {
    const nextLabel = active ? tr('desactiver', 'تعطيل') : tr('activer', 'تفعيل');
    if (!window.confirm(tr(`Voulez-vous ${nextLabel} cet utilisateur ?`, `هل تريد ${nextLabel} هذا المستخدم؟`))) {
      return;
    }
    try {
      await API.patch(`/users/${userId}`, { active: !active });
      showSnackbar(tr('Utilisateur mis a jour', 'تم تحديث المستخدم'), 'success');
      fetchUsers();
    } catch (err) {
      showSnackbar(err.response?.data?.message || tr('Erreur lors de la mise a jour', 'فشل التحديث'), 'error');
    }
  };

  const handleResetPassword = async (userId) => {
    try {
      await API.post(`/users/${userId}/password`, { password: resetPassword });
      showSnackbar(tr('Mot de passe mis a jour', 'تم تحديث كلمة المرور'), 'success');
      setResetUserId(null);
      setResetPassword('');
    } catch (err) {
      showSnackbar(err.response?.data?.message || tr('Erreur lors du reset', 'فشل إعادة التعيين'), 'error');
    }
  };

  const handleResetRandom = async (userId) => {
    try {
      const res = await API.post(`/users/${userId}/password/random`);
      setRandomPassword(res.data.password);
      setResetUserId(null);
      setResetPassword('');
      showSnackbar(tr('Mot de passe genere', 'تم إنشاء كلمة مرور'), 'success');
    } catch (err) {
      showSnackbar(err.response?.data?.message || tr('Erreur lors du reset', 'فشل إعادة التعيين'), 'error');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm(tr('Supprimer cet utilisateur ? (soft delete)', 'حذف هذا المستخدم؟ (حذف منطقي)'))) {
      return;
    }
    try {
      await API.delete(`/users/${userId}`);
      showSnackbar(tr('Utilisateur supprime', 'تم حذف المستخدم'), 'success');
      fetchUsers();
    } catch (err) {
      showSnackbar(err.response?.data?.message || tr('Erreur lors de la suppression', 'فشل الحذف'), 'error');
    }
  };

  const handleRestoreUser = async (userId) => {
    if (!window.confirm(tr('Restaurer cet utilisateur ?', 'استعادة هذا المستخدم؟'))) {
      return;
    }
    try {
      await API.post(`/users/${userId}/restore`);
      showSnackbar(tr('Utilisateur restaure', 'تمت استعادة المستخدم'), 'success');
      fetchUsers();
    } catch (err) {
      showSnackbar(err.response?.data?.message || tr('Erreur lors de la restauration', 'فشل الاستعادة'), 'error');
    }
  };

  const handleCopyPassword = async () => {
    try {
      await navigator.clipboard.writeText(randomPassword);
      showSnackbar(tr('Mot de passe copie', 'تم نسخ كلمة المرور'), 'success');
    } catch (err) {
      showSnackbar(tr('Copie impossible', 'تعذر النسخ'), 'error');
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        px: { xs: 2, md: 6 },
        py: 5,
        background: 'var(--app-bg)',
      }}
    >
      <Box
        sx={{
          mb: 6,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
        }}
      >
        <Typography
          sx={{
            fontWeight: 800,
            color: 'var(--text-primary)',
            letterSpacing: 1,
            fontSize: { xs: 28, sm: 36, md: 42 },
          }}
        >
          {tr('Employes', 'الموظفون')}
        </Typography>
        <Typography sx={{ color: 'var(--text-muted)', mt: 1, maxWidth: 500 }}>
          {tr('Gestion des utilisateurs (employes).', 'إدارة المستخدمين (الموظفين).')}
        </Typography>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          mt={4}
          justifyContent="center"
        >
          <Chip
            label={`${tr('Employes', 'الموظفون')}: ${users.length}`}
            sx={{ bgcolor: '#16a34a', color: 'var(--text-primary)' }}
          />
        </Stack>
      </Box>

      <Paper
        sx={{
          p: 4,
          mb: 6,
          borderRadius: 4,
          background:
            'var(--surface-2)',
          backdropFilter: 'blur(12px)',
          border: '1px solid var(--border-subtle)',
        }}
      >
        <Stack direction="row" justifyContent="flex-end" sx={{ mb: 2 }}>
          <Button
            variant="contained"
            onClick={() => setShowForm((v) => !v)}
            disabled={user?.role !== 'ADMIN'}
            startIcon={<AddCircleOutlineIcon />}
            sx={{
              px: 3,
              borderRadius: 3,
              fontWeight: 600,
              background: 'linear-gradient(90deg, #16a34a, #059669)',
            }}
          >
            {showForm ? tr('Fermer', 'إغلاق') : tr('Ajouter', 'إضافة')}
          </Button>
        </Stack>

        {showForm && (
          <Box component="form" onSubmit={handleSubmit}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                name="username"
                label={tr("Nom d'utilisateur", 'اسم المستخدم')}
                value={form.username}
                onChange={handleChange}
                required
                fullWidth
                disabled={user?.role !== 'ADMIN'}
                sx={{
                  backgroundColor: 'var(--input-bg)',
                  borderRadius: 2,
                }}
              />
              <TextField
                name="password"
                label={tr('Mot de passe', 'كلمة المرور')}
                type="password"
                value={form.password}
                onChange={handleChange}
                required
                fullWidth
                disabled={user?.role !== 'ADMIN'}
                sx={{
                  backgroundColor: 'var(--input-bg)',
                  borderRadius: 2,
                }}
              />
              <TextField
                select
                name="role"
                label={tr('Role', 'الدور')}
                value={form.role}
                onChange={handleChange}
                required
                fullWidth
                disabled={user?.role !== 'ADMIN'}
                sx={{
                  backgroundColor: 'var(--input-bg)',
                  borderRadius: 2,
                }}
              >
                <MenuItem value="EMPLOYEE">EMPLOYEE</MenuItem>
                <MenuItem value="ADMIN">ADMIN</MenuItem>
              </TextField>
              <Button
                type="submit"
                variant="contained"
                disabled={user?.role !== 'ADMIN'}
                sx={{
                  px: 4,
                  borderRadius: 3,
                  fontWeight: 600,
                  background: 'linear-gradient(90deg, #16a34a, #059669)',
                }}
              >
                {tr('Enregistrer', 'حفظ')}
              </Button>
            </Stack>
          </Box>
        )}

        <Box sx={{ mt: 3 }}>
          {loading ? (
            <Stack alignItems="center">
              <CircularProgress sx={{ color: 'var(--text-primary)' }} />
            </Stack>
          ) : isMobile ? (
            <Stack spacing={2}>
              {users.map((u) => (
                <Paper
                  key={u.id}
                  sx={{
                    p: 2,
                    borderRadius: 4,
                    background:
                      'var(--surface-1)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid var(--border-subtle)',
                  }}
                  >
                  <Stack spacing={0.5}>
                    <Typography sx={{ color: 'var(--text-primary)', fontWeight: 700 }}>
                      {u.username}
                    </Typography>
                    <Typography sx={{ color: 'var(--text-secondary)', fontSize: 13 }}>
                      {tr('Role', 'الدور')}: {u.role}
                    </Typography>
                    <Typography sx={{ color: 'var(--text-secondary)', fontSize: 13 }}>
                      {tr('Statut', 'الحالة')}: {u.deleted_at ? tr('Supprime', 'محذوف') : u.active ? tr('Actif', 'نشط') : tr('Inactif', 'غير نشط')}
                    </Typography>
                    <Stack direction="row" spacing={1}>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => handleToggleActive(u.id, u.active)}
                        disabled={user?.role !== 'ADMIN' || u.deleted_at}
                      >
                        {u.active ? tr('Desactiver', 'تعطيل') : tr('Activer', 'تفعيل')}
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => setResetUserId(u.id)}
                        disabled={user?.role !== 'ADMIN' || u.deleted_at}
                      >
                        {tr('Reset', 'إعادة تعيين')}
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        onClick={() => handleDeleteUser(u.id)}
                        disabled={user?.role !== 'ADMIN' || u.deleted_at}
                      >
                        {tr('Supprimer', 'حذف')}
                      </Button>
                      {u.deleted_at && (
                        <Button
                          size="small"
                          variant="contained"
                          onClick={() => handleRestoreUser(u.id)}
                          disabled={user?.role !== 'ADMIN'}
                        >
                          {tr('Restaurer', 'استعادة')}
                        </Button>
                      )}
                    </Stack>
                  </Stack>
                </Paper>
              ))}
            </Stack>
          ) : (
            <Paper
              sx={{
                borderRadius: 4,
                background:
                  'var(--surface-1)',
                backdropFilter: 'blur(10px)',
                border: '1px solid var(--border-subtle)',
                overflow: 'hidden',
              }}
            >
              <TableContainer sx={{ maxHeight: { xs: 420, md: 560 } }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      {['ID', tr('Nom', 'الاسم'), tr('Role', 'الدور'), tr('Statut', 'الحالة'), tr('Action', 'إجراء')].map((label) => (
                        <TableCell
                          key={label}
                          sx={{
                            color: 'var(--text-primary)',
                            fontWeight: 700,
                            backgroundColor: 'var(--table-header-bg)',
                            borderBottom: '1px solid var(--border-subtle)',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {label}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {users.map((u) => (
                      <TableRow
                        key={u.id}
                        sx={{
                          '&:hover': {
                            backgroundColor: 'var(--surface-3)',
                          },
                        }}
                      >
                        <TableCell sx={{ color: 'var(--text-primary)', fontWeight: 600 }}>
                          {u.id}
                        </TableCell>
                        <TableCell sx={{ color: 'var(--text-secondary)' }}>
                          {u.username}
                        </TableCell>
                        <TableCell sx={{ color: 'var(--text-secondary)' }}>
                          {u.role}
                        </TableCell>
                        <TableCell sx={{ color: 'var(--text-secondary)' }}>
                          {u.deleted_at ? tr('Supprime', 'محذوف') : u.active ? tr('Actif', 'نشط') : tr('Inactif', 'غير نشط')}
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={1}>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => handleToggleActive(u.id, u.active)}
                              disabled={user?.role !== 'ADMIN' || u.deleted_at}
                            >
                              {u.active ? tr('Desactiver', 'تعطيل') : tr('Activer', 'تفعيل')}
                            </Button>
                            <Button
                              size="small"
                              variant="contained"
                              onClick={() => setResetUserId(u.id)}
                              disabled={user?.role !== 'ADMIN' || u.deleted_at}
                            >
                              {tr('Reset', 'إعادة تعيين')}
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              onClick={() => handleDeleteUser(u.id)}
                              disabled={user?.role !== 'ADMIN' || u.deleted_at}
                            >
                              {tr('Supprimer', 'حذف')}
                            </Button>
                            {u.deleted_at && (
                              <Button
                                size="small"
                                variant="contained"
                                onClick={() => handleRestoreUser(u.id)}
                                disabled={user?.role !== 'ADMIN'}
                              >
                                {tr('Restaurer', 'استعادة')}
                              </Button>
                            )}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}
        </Box>
      </Paper>

      {resetUserId && (
        <Paper
          sx={{
            p: 3,
            borderRadius: 4,
            background:
              'linear-gradient(135deg, rgba(239,68,68,0.12), rgba(5,150,105,0.1))',
            backdropFilter: 'blur(12px)',
            border: '1px solid var(--border-subtle)',
          }}
        >
          <Typography sx={{ color: 'var(--text-primary)', fontWeight: 700, mb: 2 }}>
            {tr('Reset mot de passe', 'إعادة تعيين كلمة المرور')}
          </Typography>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <TextField
              name="resetPassword"
              label={tr('Nouveau mot de passe', 'كلمة مرور جديدة')}
              type={showResetPassword ? 'text' : 'password'}
              value={resetPassword}
              onChange={(e) => setResetPassword(e.target.value)}
              fullWidth
              sx={{ backgroundColor: 'var(--input-bg)', borderRadius: 2 }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowResetPassword((value) => !value)}
                      edge="end"
                      size="small"
                    >
                      {showResetPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Button
              variant="contained"
              onClick={() => handleResetPassword(resetUserId)}
              disabled={!resetPassword}
              sx={{
                px: 4,
                borderRadius: 3,
                fontWeight: 600,
                background: 'linear-gradient(90deg, #ef4444, #f97316)',
              }}
            >
              {tr('Enregistrer', 'حفظ')}
            </Button>
            <Button
              variant="outlined"
              onClick={() => handleResetRandom(resetUserId)}
              sx={{ px: 4, borderRadius: 3 }}
            >
              {tr('Generer', 'توليد')}
            </Button>
            <Button
              variant="outlined"
              onClick={() => {
                setResetUserId(null);
                setResetPassword('');
              }}
              sx={{ px: 4, borderRadius: 3 }}
            >
              {tr('Annuler', 'إلغاء')}
            </Button>
          </Stack>
        </Paper>
      )}

      {randomPassword && (
        <Paper
          sx={{
            p: 3,
            mt: 3,
            borderRadius: 4,
            background:
              'linear-gradient(135deg, rgba(22,163,74,0.2), rgba(16,185,129,0.1))',
            backdropFilter: 'blur(12px)',
            border: '1px solid var(--border-subtle)',
          }}
        >
          <Typography sx={{ color: 'var(--text-primary)', fontWeight: 700, mb: 1 }}>
            {tr('Mot de passe genere', 'كلمة المرور المُولدة')}
          </Typography>
          <Typography sx={{ color: 'var(--text-secondary)', fontSize: 16 }}>
            {randomPassword}
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 2 }}>
            <Button
              variant="contained"
              sx={{ borderRadius: 3 }}
              onClick={handleCopyPassword}
            >
              {tr('Copier', 'نسخ')}
            </Button>
            <Button
              variant="outlined"
              sx={{ borderRadius: 3 }}
              onClick={() => setRandomPassword('')}
            >
              {tr('Fermer', 'إغلاق')}
            </Button>
          </Stack>
        </Paper>
      )}
    </Box>
  );
}









